import { toast } from "@risalabs_frontend_org/oasis-ui-kit";
import { useCallback, useState } from "react";
import { useSelector } from "react-redux";
import { RootState } from "../../../redux/store/store";
import { getOrgIdForFetchExternalWorklist } from "../../../utils/organizationHelper";
import { updateSupportTicket } from "../apis/updateSupportTicket";
import type { SupportTicketStatus } from "../supportTicketTypes";
import { isRisaTeamEmail } from "../utils/isRisaTeamEmail";
import { mapSupportTicketStatusToApi } from "../utils/mapSupportTicketApiData";

/**
 * Options for {@link useUpdateSupportTicketStatus}.
 */
export interface UseUpdateSupportTicketStatusOptions {
  /** Currently selected support ticket id. */
  ticketId: string;
  /** Called after a status update succeeds. */
  onSuccess?: () => void | Promise<void>;
  /** Called after a ticket is archived successfully. */
  onArchiveSuccess?: () => void | Promise<void>;
}

/**
 * Return value for {@link useUpdateSupportTicketStatus}.
 */
export interface UseUpdateSupportTicketStatusReturn {
  /** Persists a lifecycle status change. Returns true on success. */
  updateStatus: (status: SupportTicketStatus) => Promise<boolean>;
  /** Archives the ticket via `is_archived: true`. Returns true on success. */
  archiveTicket: () => Promise<boolean>;
  /** Whether the status-save request is in flight. */
  isUpdatingStatus: boolean;
  /** Whether the archive request is in flight. */
  isArchiving: boolean;
}

/**
 * Options for {@link resolveUpdateContext}.
 */
interface ResolveUpdateContextOptions {
  /** When true, the caller must have a `@risalabs.ai` email. */
  requireRisaTeam?: boolean;
}

/**
 * Validates that the current user can mutate the selected ticket.
 *
 * @param userEmail - Authenticated user email.
 * @param ticketId - Currently selected ticket id.
 * @param options - Optional RISA-team requirement (status updates only).
 * @returns Organization id when valid; otherwise null after toasting an error.
 */
function resolveUpdateContext(
  userEmail: string | null | undefined,
  ticketId: string,
  options: ResolveUpdateContextOptions = {},
): string | null {
  const { requireRisaTeam = false } = options;
  if (requireRisaTeam && !isRisaTeamEmail(userEmail)) {
    toast.error("Only RISA team members can update ticket status.");
    return null;
  }

  const orgId = getOrgIdForFetchExternalWorklist();
  if (!orgId) {
    toast.error("Organization id is unavailable.");
    return null;
  }

  if (!ticketId) {
    toast.error("Select a support ticket before updating status.");
    return null;
  }

  return orgId;
}

/**
 * Handles support ticket status updates and archive with toast feedback.
 *
 * Status updates require a `@risalabs.ai` email. Archiving is allowed for any
 * authenticated user with an organization and selected ticket.
 *
 * Save and archive use separate in-flight flags so each button only reflects
 * its own request.
 *
 * @param options - Selected ticket id and optional success callback.
 * @returns Status/archive handlers and their submitting flags.
 */
export function useUpdateSupportTicketStatus(
  options: UseUpdateSupportTicketStatusOptions,
): UseUpdateSupportTicketStatusReturn {
  const { ticketId, onSuccess, onArchiveSuccess } = options;
  const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);
  const [isArchiving, setIsArchiving] = useState(false);
  const userEmail = useSelector(
    (state: RootState) => state.firebaseAuthentication.user?.email,
  );

  const updateStatus = useCallback(
    async (status: SupportTicketStatus): Promise<boolean> => {
      const orgId = resolveUpdateContext(userEmail, ticketId, {
        requireRisaTeam: true,
      });
      if (!orgId) {
        return false;
      }

      setIsUpdatingStatus(true);

      try {
        await updateSupportTicket({
          org_id: orgId,
          ticket_id: ticketId,
          status: mapSupportTicketStatusToApi(status),
        });
        toast.success("Status updated successfully.");
        await onSuccess?.();
        return true;
      } catch (error) {
        toast.error(
          error instanceof Error ? error.message : "Failed to update status.",
        );
        return false;
      } finally {
        setIsUpdatingStatus(false);
      }
    },
    [onSuccess, ticketId, userEmail],
  );

  const archiveTicket = useCallback(async (): Promise<boolean> => {
    const orgId = resolveUpdateContext(userEmail, ticketId);
    if (!orgId) {
      return false;
    }

    setIsArchiving(true);

    try {
      await updateSupportTicket({
        org_id: orgId,
        ticket_id: ticketId,
        is_archived: true,
      });
      toast.success("Ticket archived successfully.");
      await onArchiveSuccess?.();
      return true;
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : "Failed to archive ticket.",
      );
      return false;
    } finally {
      setIsArchiving(false);
    }
  }, [onArchiveSuccess, ticketId, userEmail]);

  return {
    updateStatus,
    archiveTicket,
    isUpdatingStatus,
    isArchiving,
  };
}
